/**
 * rowOps.ts - Contiguous row insert/delete methods.
 *
 * These wrap the underlying `tableOps` row ops with undo checkpointing. They have no pure
 * `Table -> Table` transform of their own — the mutation is performed by the op.
 */

import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import type {TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {saveSelection} from 'notebook/tables/store/logic/mutations/selection';

/**
 * Checkpoint a row range as the selection for undo before a structural row edit. The range spans the
 * visible (effective) columns, matching the geometry the row axis cell selects.
 */
function saveRowRangeSelection(params: TableMutationParams, start: number, end: number): void {
  const model = params.getTableModel();
  const {start: sliceStart, end: sliceEnd} = model.singleInputs.effectiveSlices;
  const startIndex: TableIndex = [Math.min(start, end), sliceStart];
  const endIndex: TableIndex = [Math.max(start, end), sliceEnd];
  saveSelection(params, {multiStartIndex: startIndex, multiEndIndex: endIndex, multiSelIndex: startIndex});
}

/**
 * Delete a contiguous range of rows.
 *
 * Checkpoints the deleted rows as the selection for undo, then deletes them. `end` defaults to
 * `start`; no-op when `start` is null.
 */
export function deleteRows(params: TableMutationParams, start: number | null, end: number | null): void {
  const lastRow = end ?? start;
  if (start == null || lastRow == null) {
    return;
  }
  saveRowRangeSelection(params, start, lastRow);
  params.tableOps.deleteTableRows(params.getTableProps().table.name, start, lastRow);
}

/**
 * Insert as many rows above the given range as the range spans.
 *
 * Checkpoints the range as the selection for undo before inserting. `end` defaults to `start`; no-op
 * when `start` is null.
 */
export function insertRowsAbove(params: TableMutationParams, start: number | null, end: number | null): void {
  const lastRow = end ?? start;
  if (start == null || lastRow == null) {
    return;
  }
  saveRowRangeSelection(params, start, lastRow);
  params.tableOps.insertTableRowsAbove(params.getTableProps().table.name, start, lastRow);
}

/**
 * Insert as many rows below the given range as the range spans.
 *
 * Checkpoints the range as the selection for undo before inserting. `end` defaults to `start`; no-op
 * when `start` is null.
 */
export function insertRowsBelow(params: TableMutationParams, start: number | null, end: number | null): void {
  const lastRow = end ?? start;
  if (start == null || lastRow == null) {
    return;
  }
  saveRowRangeSelection(params, start, lastRow);
  params.tableOps.insertTableRowsBelow(params.getTableProps().table.name, start, lastRow);
}
